import { Typography, Button, Box, Grid } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { SubGroupsClient, ModemsClient, DeviceTwinOrHeartbeatRequestedType, HeartbeatDTO, HeartbeatResponse, ModemHeartbeatResponseDTO, ModemWithOrgSiteResponseDTO } from '../generated/ApiClient'
import MUIDataTable from "mui-datatables";
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { useDispatch } from 'react-redux';
import { tableOptions as options } from '../common/Shared'
import { Admin, useIsUserInRoles } from '../hooks/useIsUserInRoles';
interface ModemsTableData {
    modemHostId: string;
    name: string;
    site: string;
    address: string;
    rcEnabled: string;
    lastHeartbeat: string;
}

type props = {
    organizationId: string
    subgroupId: string
}

function toTableData(modemData: ModemWithOrgSiteResponseDTO, heartbeat: HeartbeatResponse | undefined) {
    return {
        modemHostId: modemData.modemHostId || "",
        name: modemData.label || "",
        site: "",
        address: modemData.ipAddress || "",
        rcEnabled: modemData.remoteConnectivityEnabled === true ? 'True' : 'False',
        lastHeartbeat: (heartbeat?.Fields as HeartbeatDTO | undefined)?.lastOnlineTime || ''
    }
}

export default function SubgroupModems(props: props) {
    const [modemsData, setModemsData] = useState<ModemWithOrgSiteResponseDTO[]>([]);
    const [heartbeatsData, setHeartbeatsData] = useState<ModemHeartbeatResponseDTO[]>([]);
    const subgroupId = props.subgroupId
    const isAdmin = useIsUserInRoles(Admin)

    const dispatch = useDispatch()

    const removeModems = async (modemIds: string[]) => {
        try {
            if (subgroupId) {
                await Promise.all(modemIds.map((modemId) => removeModem(modemId)));
                dispatch(snackbar.addMessage({ title: "Success", message: `Modems successfully removed`, alertType: 'success' }))
                const newModemData = modemsData.filter(x => !modemIds.includes(x.modemHostId));
                setModemsData(newModemData)
            }
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
        }
    }

    const removeModem = async (modemId: string) => {
        try {
            await new SubGroupsClient().removeModem3(subgroupId, modemId);
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
        }
    }

    const tableOptions = {
        ...options,
        selectableRowsHideCheckboxes: !isAdmin,
        onRowsDelete: ((rowsDeleted: { lookup: any; data: { index: number; dataIndex: number; }[]; }, data: ModemWithOrgSiteResponseDTO[]) => {
            const selectedIndexs = rowsDeleted.data.map((row) => row.dataIndex);
            const modemIds: string[] = selectedIndexs.map((index) => modemsData![index].modemHostId);
            removeModems(modemIds);
        })
    }

    const columns = [
        {
            name: "modemHostId",
            label: "Modem Host Id",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: "name",
            label: "Name",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: "site",
            label: "Site",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: "address",
            label: "Address",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: "rcEnabled",
            label: "Remote Connectivity Enabled",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: "lastHeartbeat",
            label: "Last Heartbeat",
            options: {
                sort: true,
                filter: true
            }
        },
        {
            name: 'detailsButton',
            label: 'View',
            options: {
                sort: false,
                filter: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const link = `/modems/${modemsData[dataIndex].modemHostId}`;
                    return <Button component={RouterLink} variant='contained' size='small' to={link}>View</Button>
                }
            }
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (subgroupId) {
                    const modemsData = await new SubGroupsClient().getAllModems2(subgroupId);
                    const heartbeats = await new ModemsClient().getHeartbeats(DeviceTwinOrHeartbeatRequestedType.Simple, { modemIds: modemsData.map(s => s.modemHostId) })
                    setModemsData(modemsData);
                    setHeartbeatsData(heartbeats)
                }
            }
            catch (error) {
                dispatch(snackbar.addError(error, undefined, 'Error fetching modems'));
            }
        }
        fetchData();
    }, [subgroupId])
    const heartbeatMap = Object.fromEntries(heartbeatsData.map(s => [s.modemHostId, s.heartbeat]))
    return (
        <>
            {isAdmin &&
                <Grid container item direction='column' spacing={2} alignItems='flex-end'>
                    <Box my={2}>
                        <Button component={RouterLink} variant='contained' color='primary' size='medium' to={`/organizations/${props.organizationId}/subgroups/${subgroupId}/addModem`}>+ Add</Button>
                    </Box>
                </Grid>}
            <MUIDataTable
                title={""}
                data={modemsData.map(m => toTableData(m, heartbeatMap[m.modemHostId]))}
                columns={columns}
                options={tableOptions}
            />
        </>
    )
}